import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { WarningCircle } from '@phosphor-icons/react';
import { useInputId } from './hooks';

const FormInput = React.forwardRef(
  (
    {
      id: defaultId,
      icon: Icon,
      label,
      labelClassName,
      name,
      onChange,
      onSetFieldValue,
      value,
      error,
      required,
      disabled,
      upperCase,
      ...props
    },
    ref,
  ) => {
    const [id] = useInputId(defaultId);
    const handleChange = ({ target }) => {
      if (typeof onChange === 'function') {
        onChange((state) => ({
          ...state,
          [name]: target.value,
        }));
      }
      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(name, target.value);
      }
    };
    const showIcon = Icon || error;
    const isError = !!error;
    return (
      <div className="space-y-1.5">
        {label && (
          <label
            htmlFor={id}
            className={cn(
              'block text-xs font-semibold text-neutral-700',
              labelClassName,
            )}
          >
            <span>{label}</span>
            {required && <span className="ml-1 text-danger-600">*</span>}
          </label>
        )}
        <div
          className={cn(
            'relative h-9 flex items-center border rounded px-3 z-[1]',
            'focus-within:ring-2 focus-within:ring-offset-2',
            // eslint-disable-next-line no-nested-ternary
            disabled
              ? 'border-neutral-200 bg-neutral-50'
              : isError
              ? 'border-danger-600 bg-danger-50 focus-within:ring-danger-600'
              : 'border-neutral-300 bg-white focus-within:border-brand-600 focus-within:ring-brand-600',
          )}
        >
          <input
            id={id}
            ref={ref}
            type="text"
            name="name"
            className={cn(
              'block w-full border-0 p-0 bg-transparent focus:ring-0 text-sm focus:outline-none',
              'placeholder:text-neutral-500',
              disabled ? 'text-neutral-400' : 'text-neutral-900',
              showIcon ? 'pr-10' : '',
              upperCase ? 'uppercase' : '',
            )}
            onChange={handleChange}
            value={value || ''}
            disabled={disabled}
            {...props}
          />
          {showIcon && (
            <div
              className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
              title={error || ''}
            >
              {error ? (
                <WarningCircle
                  size={16}
                  className="text-danger-600"
                  aria-hidden="true"
                />
              ) : (
                <Icon
                  size={16}
                  className="text-neutral-500"
                  aria-hidden="true"
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

FormInput.defaultProps = {
  label: '',
  labelClassName: '',
  id: '',
  icon: false,
  type: 'text',
  error: null,
  disabled: false,
  required: false,
  onChange: false,
  onSetFieldValue: false,
  upperCase: false,
};

FormInput.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  labelClassName: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.bool]),
  error: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSetFieldValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  upperCase: PropTypes.bool,
};

export default React.memo(FormInput);
